import type { HotelSearchData, HotelSearchProgress } from '@/types/hotel-search';

export interface MunicipalityEnrichmentStatus {
  municipality: string;
  done: number;
  /** Hotéis já presentes no raw_data (0 se destino ainda não persistido). */
  inRawData: number;
  isProcessing: boolean;
}

/**
 * Destinos em enriquecimento neste lote (via progress_log).
 * Destinos fora deste conjunto permanecem utilizáveis se já estiverem no raw_data.
 */
export function getMunicipalityEnrichmentStatus(
  progress: HotelSearchProgress,
  searchData: HotelSearchData | null,
): MunicipalityEnrichmentStatus[] {
  if (progress.status !== 'processing') return [];

  const byMunicipality = progress.progress_log.reduce<Record<string, number>>((acc, entry) => {
    acc[entry.municipality] = (acc[entry.municipality] ?? 0) + 1;
    return acc;
  }, {});

  const globalDone = progress.progress_done >= progress.progress_total && progress.progress_total > 0;

  return Object.entries(byMunicipality).map(([municipality, done]) => {
    const dest = searchData?.destinations.find(d => d.municipality === municipality);
    const inRawData = dest?.hotels.length ?? 0;
    // Enquanto o status global for processing, destinos do lote atual ficam marcados como processing
    // (mesmo após todos os hotéis do log), até o worker finalizar raw_data + status=completed.
    const isProcessing = !globalDone || inRawData < done;

    return { municipality, done, inRawData, isProcessing };
  });
}

export function getProcessingMunicipalitySet(
  progress: HotelSearchProgress,
  searchData: HotelSearchData | null,
): Set<string> {
  return new Set(
    getMunicipalityEnrichmentStatus(progress, searchData)
      .filter(s => s.isProcessing)
      .map(s => s.municipality),
  );
}

/** Municípios para abas do header: raw_data + destinos só no progress_log. */
export function getHeaderMunicipalities(
  searchData: HotelSearchData | null,
  progress: HotelSearchProgress | null,
): string[] {
  const fromRaw = searchData?.destinations.map(d => d.municipality) ?? [];
  const fromProgress = progress?.progress_log.map(e => e.municipality) ?? [];
  return [...new Set([...fromRaw, ...fromProgress])];
}

export function isMunicipalityBlocked(
  municipality: string | null,
  processingMunicipalities: Set<string>,
): boolean {
  if (!municipality) {
    // "Todos" — bloqueado só se TODOS os destinos visíveis estiverem em processing
    return false;
  }
  return processingMunicipalities.has(municipality);
}

/** Em "Todos", excluir hotéis de destinos ainda em enriquecimento. */
export function filterHotelsForProcessing(
  municipality: string | null,
  processingMunicipalities: Set<string>,
): boolean {
  return (hotelMunicipality: string) => {
    if (municipality) return true;
    return !processingMunicipalities.has(hotelMunicipality);
  };
}
